import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useFieldArray } from 'react-hook-form';
import { closeModal } from 'data/reducers/modalReducer';
import {
	createPortfolio,
	updatePortfolio,
	fetchPortfolioById,
	clearPortfolioErrors,
	addPropertyToPortfolioWithState,
} from 'data/reducers/portfoliosManagementReducer';
import { fetchAdminUsers } from 'data/reducers/adminUsersReducer';
import { clearUserSearch } from 'data/reducers/userSearchReducer';
import { showToast } from 'data/reducers/toastReducer';
import StandardModal from './StandardModal';
import { FormInput, FormSelect } from '../components/Form';
import { isValidEmail } from 'utils/validators';
import { useOrganizationContext } from 'src/contexts/OrganizationContext';
import './AddPortfolioModal.css';

const AddPortfolioModal = ({ options }) => {
	const dispatch = useDispatch();
	const { activeOrganization } = useOrganizationContext();
	const {
		isCreating,
		isUpdating,
		createError,
		updateError,
		selectedPortfolio,
		isLoadingSelected,
	} = useSelector((state) => state.portfoliosManagement);

	// Check if this is edit mode
	const isEditMode = options?.portfolioId;
	const portfolioId = options?.portfolioId;
	const propertyIdToAdd = options?.propertyId; // Property to add after creation

	const onConfirm = options?.onConfirm; // Callback after successful creation/edit

	// React Hook Form setup
	const { control, handleSubmit, reset } = useForm({
		defaultValues: {
			name: '',
			company: '',
			users: [],
			// users: [{ email: '', type: 'viewer' }],
		},
		mode: 'onChange',
	});

	// Clear any existing errors and fetch users when the modal opens
	useEffect(() => {
		dispatch(clearPortfolioErrors());
		// Clear any existing user search results
		dispatch(clearUserSearch());
		// Fetch all users for the dropdown (with a large per_page to get all users)
		dispatch(fetchAdminUsers('name|asc', 1, 1000));

		// If in edit mode, fetch the portfolio data
		if (isEditMode && portfolioId) {
			dispatch(fetchPortfolioById(portfolioId));
		}
	}, [dispatch, isEditMode, portfolioId]);

	// Cleanup user search when modal closes
	useEffect(() => {
		return () => {
			dispatch(clearUserSearch());
		};
	}, [dispatch]);

	// Pre-fill form with portfolio data when editing
	useEffect(() => {
		if (isEditMode && selectedPortfolio && !isLoadingSelected) {
			// Map portfolio users to form format
			const portfolioUsers =
				selectedPortfolio.users?.length > 0
					? selectedPortfolio.users.map((user) => ({
							email: user.email,
							type: user.role === 'admin' ? 'admin' : 'viewer',
						}))
					: [{ email: '', type: 'viewer' }];

			// Reset form with portfolio data
			reset({
				name: selectedPortfolio.name || '',
				company: selectedPortfolio.company || '',
				users: portfolioUsers,
			});
		}
	}, [isEditMode, selectedPortfolio, isLoadingSelected, reset]);

	// Field array for dynamic user management
	const { fields, append, remove } = useFieldArray({
		control,
		name: 'users',
	});

	// User type options
	const userTypeOptions = [
		{ value: 'viewer', label: 'Viewer' },
		{ value: 'admin', label: 'Admin' },
	];

	// Add new user row
	const addUser = () => {
		append({ email: '', type: 'viewer' });
	};

	// Remove user row
	const removeUser = (index) => {
		remove(index);
	};

	// Form submission handler
	const onSubmit = async (data) => {
		try {
			// Prepare portfolio data for API
			const portfolioData = {
				name: data.name,
				company: data.company,
				users: data.users
					.filter((user) => user.email && isValidEmail(user.email))
					.map((user) => ({
						email: user.email,
						type: user.type,
					})),
				status: 'active', // Default status
			};

			let result;
			if (isEditMode) {
				// Update existing portfolio
				result = await dispatch(updatePortfolio(portfolioId, portfolioData));
			} else {
				// Create new portfolio
				result = await dispatch(createPortfolio(portfolioData));
			}

			if (onConfirm) {
				onConfirm();
			}

			if (result) {
				// If we have a property to add and we're creating (not editing)
				if (!isEditMode && propertyIdToAdd && result.id) {
					try {
						// Add the property to the newly created portfolio
						await dispatch(
							addPropertyToPortfolioWithState(result.id, propertyIdToAdd),
						);

						// Show enhanced success message
						dispatch(
							showToast({
								title: 'Success!',
								body: `Portfolio "${data.name}" has been created and the property has been added successfully.`,
								type: 'success',
								duration: 5000,
							}),
						);
					} catch (propertyError) {
						console.error(
							'Error adding property to new portfolio:',
							propertyError,
						);
						// Show success for portfolio creation but warn about property
						dispatch(
							showToast({
								title: 'Portfolio Created',
								body: `Portfolio "${data.name}" was created successfully, but we couldn't add the property. You can add it manually from the portfolio.`,
								type: 'warning',
								duration: 6000,
							}),
						);
					}
				} else {
					// Standard success message for creation/editing without property
					dispatch(
						showToast({
							title: 'Success!',
							body: `Portfolio "${data.name}" has been ${
								isEditMode ? 'updated' : 'created'
							} successfully.`,
							type: 'success',
						}),
					);
				}

				// Close modal on success
				dispatch(closeModal());
			}
		} catch (error) {
			console.error(
				`Error ${isEditMode ? 'updating' : 'creating'} portfolio:`,
				error,
			);
			// Error is handled by the reducer and displayed in the form
		}
	};

	const handleCancel = () => {
		dispatch(closeModal());
	};

	// Get the current error and loading state
	const currentError = isEditMode ? updateError : createError;
	const isLoading = isEditMode ? isUpdating : isCreating;

	return (
		<StandardModal
			title={isEditMode ? 'Edit Portfolio' : 'Create Portfolio'}
			className="add-portfolio-modal violerts-v3-root"
			size="md"
			onClose={handleCancel}
			closeOnOutsideClick
			noFooter
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="portfolio-modal-body">
					{activeOrganization && !isEditMode && (
						<div className="portfolio-org-banner">
							<i className="bi bi-info-circle-fill" />
							<span>
								This portfolio will be created in{' '}
								<strong>{activeOrganization.name}</strong>
							</span>
						</div>
					)}

					{/* Show loading state when fetching portfolio data */}
					{isEditMode && isLoadingSelected && (
						<div className="mb-4 text-center py-3">
							<div className="spinner-border text-primary" role="status">
								<span className="visually-hidden">Loading portfolio...</span>
							</div>
							<p className="mt-2 text-muted small">Loading portfolio data...</p>
						</div>
					)}

					{currentError && (
						<div className="portfolio-modal-error">
							<i className="bi bi-exclamation-triangle-fill" />
							<span>
								{typeof currentError === 'string'
									? currentError
									: currentError.message ||
										currentError.data?.message ||
										`Error ${
											isEditMode ? 'updating' : 'creating'
										} portfolio. Please try again.`}
							</span>
						</div>
					)}

					{/* Portfolio Name and Company Name - stacked single column like Figma */}
					<div className="portfolio-form-fields">
						<div>
							<FormInput
								name="name"
								control={control}
								label="Portfolio Name"
								placeholder="Enter portfolio name"
								required
								maxLength={100}
								minLength={3}
							/>
						</div>
						<div>
							<FormInput
								name="company"
								control={control}
								label="Company/Organization Name"
								placeholder="Enter company/organization name"
								maxLength={100}
								minLength={2}
							/>
						</div>
					</div>

					{!isEditMode && (
						<div className="portfolio-members-section">
							<div className="portfolio-members-header">
								<h6 className="portfolio-members-title">Members</h6>
							</div>

							{fields.length > 0 && (
								<div className="portfolio-members-role-label">
									<span className="portfolio-role-label-email">User</span>
									<span className="portfolio-role-label-role">Role</span>
								</div>
							)}

							{fields.map((field, index) => (
								<div key={field.id} className="portfolio-member-row">
									<div className="portfolio-member-email-wrap">
										<FormInput
											name={`users.${index}.email`}
											control={control}
											type="email"
											placeholder="Enter member email address..."
											required
											validate={{
												isValidEmail: (value) =>
													isValidEmail(value) ||
													'Please enter a valid email address',
											}}
											className="mb-0"
										/>
									</div>
									<div className="portfolio-member-role-wrap">
										<FormSelect
											name={`users.${index}.type`}
											control={control}
											options={userTypeOptions}
											defaultValue="viewer"
											placeholder={null}
											className="mb-0"
										/>
									</div>
									{fields.length > 0 && (
										<button
											type="button"
											className="portfolio-remove-user-btn"
											onClick={() => removeUser(index)}
											title="Remove user"
											aria-label="Remove user"
										>
											<i className="bi bi-trash" />
										</button>
									)}
								</div>
							))}

							<button
								type="button"
								className="portfolio-add-member-btn"
								onClick={addUser}
							>
								<i className="bi bi-plus-lg" />
								<span>Add Users</span>
							</button>

							<div className="portfolio-members-help">
								<i className="bi bi-info-circle" />
								<span>
									Add users who will have access to this portfolio. Admins can manage properties; Viewers have read-only access.
								</span>
							</div>
						</div>
					)}
				</div>

				<div className="portfolio-modal-footer">
					<button
						type="button"
						className="portfolio-cancel-btn"
						onClick={handleCancel}
						disabled={isLoading}
					>
						Close
					</button>
					<button
						type="submit"
						className="portfolio-submit-btn"
						disabled={isLoading || (isEditMode && isLoadingSelected)}
					>
						{isLoading ? (
							<>
								<span
									className="spinner-border spinner-border-sm me-1"
									role="status"
									aria-hidden="true"
								/>
								<span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
							</>
						) : (
							<>
								<i
									className={`bi ${
										isEditMode ? 'bi-check-lg' : 'bi-plus-lg'
									}`}
								/>
								<span>{isEditMode ? 'Update Portfolio' : 'Create Portfolio'}</span>
							</>
						)}
					</button>
				</div>
			</form>
		</StandardModal>
	);
};

export default AddPortfolioModal;
